import { CompTableColumns } from '@aloft/toolkit-shared';
import { Flex, Icon, Modal, Switch, Typography } from '@aloft/toolkit-sui';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useEffect, useState } from 'react';

import {
  ColumnSettingsItem,
  getColumnSettingsItems,
  reorderColumnSettings,
  setColumnSettingsVisibility,
  toPersistedCompTableColumns,
} from '../utils/columnSettings.ts';

type ColumnSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  columns: CompTableColumns;
  save: (columns: CompTableColumns, saveAsDefault?: boolean) => Promise<void>;
  isSaving?: boolean;
};

const SortableColumnRow = ({
  item,
  onVisibilityChange,
}: {
  item: ColumnSettingsItem;
  onVisibilityChange: (id: ColumnSettingsItem['id'], visible: boolean) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !item.canReorder,
  });

  return (
    <Flex
      ref={setNodeRef}
      alignItems="center"
      gap="8px"
      width="100%"
      transform={CSS.Transform.toString(transform)}
      __dangerDoNotUseSx={{ transition, opacity: isDragging ? 0.5 : 1 }}
    >
      {item.canReorder ? (
        <div
          {...attributes}
          {...listeners}
          data-testid={`column-drag-${item.id}`}
          style={{ display: 'flex', cursor: 'grab', touchAction: 'none' }}
        >
          <Icon icon="draggable" size="md" color="textSecondary" />
        </div>
      ) : (
        <Flex width="16px" flexShrink={0} />
      )}
      <Flex flexGrow={1} minWidth={0}>
        <Typography variant="h3">{item.label}</Typography>
      </Flex>
      {item.canHide && (
        <Switch
          id={`column-visibility-${item.id}`}
          label=""
          fullWidth={false}
          value={item.visible}
          onChange={(visible) => onVisibilityChange(item.id, visible)}
          __testId={`column-visibility-${item.id}`}
        />
      )}
    </Flex>
  );
};

export const ColumnSettingsModal = ({
  isOpen,
  onClose,
  columns,
  save,
  isSaving = false,
}: ColumnSettingsModalProps) => {
  const [draft, setDraft] = useState(columns);
  const items = getColumnSettingsItems(draft);

  useEffect(() => {
    if (isOpen) setDraft(columns);
  }, [isOpen, columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const persist = async (saveAsDefault: boolean) => {
    await save(toPersistedCompTableColumns(draft), saveAsDefault);
    onClose();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((current) =>
      reorderColumnSettings(
        current,
        String(active.id) as ColumnSettingsItem['id'],
        String(over.id) as ColumnSettingsItem['id'],
      ),
    );
  };

  return (
    <Modal
      size="sm"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc
      showCloseButton={false}
      title="Column Settings"
      secondaryAction={{
        text: 'Save as Default',
        variant: 'inline',
        disabled: isSaving,
        onClick: () => {
          void persist(true);
        },
      }}
      primaryAction={{
        text: 'Close',
        disabled: isSaving,
        onClick: () => {
          void persist(false);
        },
      }}
    >
      <Flex
        flexDirection="column"
        gap="16px"
        maxHeight="360px"
        overflowY="auto"
        paddingY="8px"
      >
        <Typography variant="subtitle1">First</Typography>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <Flex flexDirection="column" gap="24px" width="100%">
              {items.map((item) => (
                <SortableColumnRow
                  key={item.id}
                  item={item}
                  onVisibilityChange={(id, visible) => {
                    setDraft((current) => setColumnSettingsVisibility(current, id, visible));
                  }}
                />
              ))}
            </Flex>
          </SortableContext>
        </DndContext>
        <Typography variant="subtitle1">Last</Typography>
      </Flex>
    </Modal>
  );
};
